import React, { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { useRegTheme } from "../theme";
import { addItem } from "../../../Redux/features/WebRegister/cartSlice";
import { phpGiftCards } from "../data/api";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import NumericInput from "../NumericInput";

// Issue (sell/load) a gift card — port of issueGiftCardDialog: scan/enter a card number, validate it
// does NOT already exist on the server and is not already being issued in this cart, then add a
// TAXLESS custom line "Gift Card(NUMBER)" at the entered amount. At save, giftcard_json carries it
// as gift_type "credit" (the server creates/loads the card). Amount prefills from the home keypad
// (Android getInputAmount).
export default function IssueGiftCardModal({ initialAmount, onIssued, onClose }) {
  const t = useRegTheme();
  const dispatch = useDispatch();
  const store = useStore();
  const [number, setNumber] = useState("");
  const [amount, setAmount] = useState(Number(initialAmount) > 0 ? Number(initialAmount) : 0);
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    const num = number.trim();
    if (!num) return ToastifyAlert("Scan or enter a gift card number", "error");
    const amt = Number(amount) || 0;
    if (amt <= 0) return ToastifyAlert("Enter an amount", "error");
    const name = `Gift Card(${num})`;
    // already being issued in this cart?
    if (store.getState().webRegisterCart.lines.some((l) => String(l.orderName) === name)) {
      return ToastifyAlert("Gift card already added to this cart", "error");
    }
    setBusy(true);
    try {
      // must NOT already exist on the server (issuing creates it)
      const res = await phpGiftCards();
      const rows = res && Array.isArray(res.result) ? res.result : [];
      if (rows.some((g) => String(g.number).trim() === num)) {
        return ToastifyAlert("Gift card already exists", "error");
      }
      dispatch(
        addItem({
          productId: "0",
          name,
          unitPrice: amt,
          isCustomLineItem: true,
          isGiftCard: true,
          taxList: [], // gift cards are sold WITHOUT tax (Android "product without tax")
          model: { title: name, price: amt, categoryName: "Gift Card" },
          qty: 1,
        })
      );
      ToastifyAlert("Gift card added to cart", "success");
      if (onIssued) onIssued();
      onClose();
    } catch {
      ToastifyAlert("Gift card lookup failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const s = styles(t);
  return (
    <div className="qv-dlg-overlay" style={s.overlay} onClick={onClose}>
      <div className="qv-dlg-card" style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <span>Issue gift card</span>
          <button style={s.x} onClick={onClose}>✕</button>
        </div>
        <div style={s.body}>
          <div style={s.label}>Gift card number</div>
          <input
            style={s.input}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Scan or enter"
            autoFocus
          />
          <div style={s.label}>Amount to load</div>
          <NumericInput style={s.input} value={amount} onChange={setAmount} decimals={2} onEnter={issue} />
          <button style={s.issue} disabled={busy} onClick={issue}>{busy ? "…" : "Issue gift card"}</button>
        </div>
      </div>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  card: { width: "100%", maxWidth: 400, background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden" },
  head: { padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.surfaceAlt || "#0f1a2f" },
  x: { border: "none", background: "none", cursor: "pointer", fontSize: 18, color: t.textMuted },
  body: { padding: 18, display: "flex", flexDirection: "column", gap: 10 },
  label: { fontSize: 13, color: t.textMuted },
  input: { width: "100%", height: 48, borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputText, padding: "0 14px", fontSize: 17, fontWeight: 500, outline: "none", boxSizing: "border-box" },
  issue: { height: 50, marginTop: 6, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" },
});
